import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

// Add wishlist event name to the shared ThemeEvents namespace
ThemeEvents.wishlistUpdate = 'wishlist:update';

// ─── WishlistManager ────────────────────────────────────────────────────────

export class WishlistManager {
  static #STORAGE_KEY = 'wishlistProductIds';
  static #TOKEN_KEY = 'wishlistCustomerToken';

  // ── localStorage ───────────────────────────────────────────────────────────

  // Storage shape: [{ id: "123", handle: "my-product" }, ...]
  // Legacy ["123", ...] arrays are normalized on read so existing wishlists
  // don't get wiped when this code ships.
  static #normalize(raw) {
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return { id: String(item), handle: null };
        }
        if (item && typeof item === 'object' && item.id != null) {
          return { id: String(item.id), handle: item.handle ?? null };
        }
        return null;
      })
      .filter(Boolean);
  }

  static getProducts() {
    try {
      return this.#normalize(JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]'));
    } catch {
      return [];
    }
  }

  static getIds() {
    return this.getProducts().map((p) => p.id);
  }

  static #save(products) {
    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(products));
  }

  static has(productId) {
    const id = String(productId);
    return this.getProducts().some((p) => p.id === id);
  }

  static add(productId, handle = null) {
    const id = String(productId);
    const products = this.getProducts();
    const existing = products.find((p) => p.id === id);
    if (existing) {
      if (!existing.handle && handle) existing.handle = handle;
      else return;
    } else {
      products.unshift({ id, handle: handle ?? null });
    }
    this.#save(products);
    this.#emit();
    this.#syncToMetafield(products);
  }

  static remove(productId) {
    const id = String(productId);
    const products = this.getProducts().filter((p) => p.id !== id);
    this.#save(products);
    this.#emit();
    this.#syncToMetafield(products);
  }

  static toggle(productId, handle = null) {
    this.has(productId) ? this.remove(productId) : this.add(productId, handle);
  }

  // Fill in a missing handle for an already-wishlisted product without
  // toggling state. Lets a PDP/card visit upgrade legacy ID-only entries.
  static upgradeHandle(productId, handle) {
    if (!handle) return;
    const id = String(productId);
    const products = this.getProducts();
    const existing = products.find((p) => p.id === id);
    if (!existing || existing.handle === handle) return;
    existing.handle = handle;
    this.#save(products);
    this.#syncToMetafield(products);
  }

  // ── Customer access token ──────────────────────────────────────────────────

  static getToken() {
    return localStorage.getItem(this.#TOKEN_KEY);
  }

  static setToken(token) {
    if (token) localStorage.setItem(this.#TOKEN_KEY, token);
  }

  static clearToken() {
    localStorage.removeItem(this.#TOKEN_KEY);
  }

  // ── Storefront API ─────────────────────────────────────────────────────────

  static async #gql(query, variables = {}) {
    const { storefrontToken, storefrontUrl } = Theme.wishlist;
    if (!storefrontToken) return null;

    try {
      const res = await fetch(storefrontUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontToken,
        },
        body: JSON.stringify({ query, variables }),
      });
      if (!res.ok) return null;
      return res.json();
    } catch {
      return null;
    }
  }

  static async #syncToMetafield(products) {
    const token = this.getToken();
    if (!token) return;

    const data = await this.#gql(
      `mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
        customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
          userErrors { field message }
        }
      }`,
      {
        customerAccessToken: token,
        customer: {
          metafields: [
            {
              namespace: 'wishlist',
              key: 'product_ids',
              type: 'json',
              value: JSON.stringify(products),
            },
          ],
        },
      }
    );

    const errors = data?.data?.customerUpdate?.userErrors;
    if (errors?.length && errors.some((e) => e.message?.toLowerCase().includes('token'))) {
      this.clearToken();
    }
  }

  // Merge the server-rendered wishlist (from Liquid) with localStorage on page load.
  static async mergeServerState() {
    if (!Theme.wishlist.customerLoggedIn) return;

    const serverProducts = this.#normalize(Theme.wishlist.customerWishlistIds || []);
    const localProducts = this.getProducts();

    const map = new Map();
    for (const p of serverProducts) map.set(p.id, { ...p });
    for (const p of localProducts) {
      const existing = map.get(p.id);
      if (!existing) map.set(p.id, { ...p });
      else if (!existing.handle && p.handle) existing.handle = p.handle;
    }

    const merged = [...map.values()];
    this.#save(merged);
    this.#emit();

    // Push back if we added local-only items, or upgraded any handles.
    const serverCount = serverProducts.length;
    const serverHandleCount = serverProducts.filter((p) => p.handle).length;
    const mergedHandleCount = merged.filter((p) => p.handle).length;
    if (merged.length !== serverCount || mergedHandleCount !== serverHandleCount) {
      await this.#syncToMetafield(merged);
    }
  }

  // Intercept the Shopify classic account login form to acquire a customer
  // access token for Storefront API metafield writes. The native form submit
  // is not blocked — this runs in parallel.
  static interceptLoginForm() {
    const form = document.querySelector('form[action*="/account/login"]');
    if (!form) return;

    form.addEventListener('submit', () => {
      const emailInput = form.querySelector('[name="customer[email]"]');
      const passwordInput = form.querySelector('[name="customer[password]"]');
      if (!emailInput || !passwordInput) return;
      this.#acquireToken(emailInput.value, passwordInput.value).catch(() => {});
    });

    form.addEventListener('submit', () => {
      const logoutForm = document.querySelector('form[action*="/account/logout"]');
      if (logoutForm) {
        logoutForm.addEventListener('submit', () => this.clearToken(), { once: true });
      }
    });
  }

  static async #acquireToken(email, password) {
    const data = await this.#gql(
      `mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
        customerAccessTokenCreate(input: $input) {
          customerAccessToken { accessToken }
          userErrors { field message }
        }
      }`,
      { input: { email, password } }
    );

    const token =
      data?.data?.customerAccessTokenCreate?.customerAccessToken?.accessToken;
    if (token) this.setToken(token);
  }

  // ── Event dispatch ─────────────────────────────────────────────────────────

  static #emit() {
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.wishlistUpdate, { bubbles: true })
    );
  }
}

// ─── WishlistButton ──────────────────────────────────────────────────────────
// Renders as a heart toggle button on product cards and the PDP.
// Relies on: data-product-id attribute, refs: button

class WishlistButton extends Component {
  requiredRefs = ['button'];

  get productId() {
    return this.dataset.productId;
  }

  get productHandle() {
    return this.dataset.productHandle || null;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#render);
    this.#render();
    // Backfill the handle for any legacy wishlist entry that only stored the ID.
    if (this.productHandle) {
      WishlistManager.upgradeHandle(this.productId, this.productHandle);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#render);
  }

  // Called via on:click="/toggle" component event binding
  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    WishlistManager.toggle(this.productId, this.productHandle);
  }

  #render = () => {
    const isWishlisted = WishlistManager.has(this.productId);
    const btn = this.refs.button;
    if (!btn) return;
    btn.setAttribute('aria-pressed', String(isWishlisted));
    btn.setAttribute(
      'aria-label',
      isWishlisted
        ? Theme.wishlist.translations.remove_from_wishlist
        : Theme.wishlist.translations.add_to_wishlist
    );
    this.classList.toggle('wishlist-button--active', isWishlisted);
  };
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

// ─── WishlistIcon ────────────────────────────────────────────────────────────
// Renders as the header icon with a count badge.
// Relies on: refs wishlistBubble, wishlistCount (both optional).

class WishlistIcon extends Component {
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#render);
    this.#setupObserver();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#render);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  // Watch the bubble's class attribute so that when morphSection resets
  // visually-hidden (after a cart add), #render immediately restores it.
  #setupObserver() {
    const bubble = this.querySelector('[ref="wishlistBubble"]');
    if (!bubble) return;
    this.#observer = new MutationObserver(this.#render);
    this.#observer.observe(bubble, { attributes: true, attributeFilter: ['class'] });
  }

  #render = () => {
    const count = WishlistManager.getIds().length;
    const bubble = this.querySelector('[ref="wishlistBubble"]');
    const countEl = this.querySelector('[ref="wishlistCount"]');
    if (bubble) bubble.classList.toggle('visually-hidden', count === 0);
    if (countEl) countEl.textContent = count > 99 ? '99+' : String(count);
  };
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}

// ─── Init ────────────────────────────────────────────────────────────────────

WishlistManager.mergeServerState();
WishlistManager.interceptLoginForm();

// Attach logout token clear
const logoutForm = document.querySelector('form[action*="/account/logout"]');
if (logoutForm) {
  logoutForm.addEventListener('submit', () => WishlistManager.clearToken(), {
    once: true,
  });
}

// Auto-remove from wishlist when a wishlisted product is added to cart from any page (PDP, quick-add, product listing).
document.addEventListener(ThemeEvents.cartUpdate, (e) => {
  const productId = e.detail?.data?.productId;
  if (productId && WishlistManager.has(productId)) {
    WishlistManager.remove(productId);
  }
});
